/**
 * Logiciels connectés (« Consulter une API ») : une connexion du compte, que
 * les agents cochés utilisent. Pendant l'appel, l'agent interroge le logiciel du client
 * et reçoit sa réponse telle quelle. La clé est chiffrée et ne revient jamais au
 * dashboard (seulement « •••• 3f2a »). L'adresse peut contenir des trous {parametre}.
 * Un modèle tout prêt ne fait que préremplir cette configuration.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/** `numero_appelant_ou_donne`: the caller's number, unless they give another one (said or typed). */
export type ApiParamSource = 'numero_appelant' | 'demande' | 'numero_appelant_ou_donne'

export interface ApiActionParam {
  name: string
  source: ApiParamSource
  description: string
  /** « demande » seulement ; absent = obligatoire. Un facultatif n'est
   *  demandé que si la première consultation ne trouve rien. */
  required?: boolean
}

/** La configuration d'une connexion : tout sauf la clé. */
export interface ApiActionConfig {
  name: string
  usage_hint: string
  url: string
  method: 'GET' | 'POST'
  timeout_s: number
  auth_header: string
  signing_enabled: boolean
  parameters: ApiActionParam[]
  text_error: string
  is_active: boolean
}

export interface ApiAction extends ApiActionConfig {
  id: number
  /** Agents qui utilisent la connexion */
  agent_ids: number[]
  /** Le modèle qui l'a préremplie, s'il y en a un (sans effet ensuite) */
  modele: string
  slug: string
  has_auth: boolean
  auth_value_hint: string
  /** Le secret de signature a déjà été donné par « Créer un secret » :
   *  en créer un autre remplace l'ancien (confirmation dans la page). */
  signing_secret_given: boolean
}

export interface ApiActionInput extends Partial<ApiActionConfig> {
  agent_ids?: number[]
  /** Absent = on garde la clé ; '' = on la retire */
  auth_value?: string
  /** La clé collée (préfixée « Bearer » par le serveur si besoin) */
  cle?: string
}

/** Ce que l'agent recevrait, et l'adresse qui serait appelée (sans la clé). */
export interface ApiActionTestResult {
  url: string
  status: 'found' | 'none' | 'error'
  message: string
  /** La réponse de l'API telle quelle (JSON réémis, ou texte) */
  response?: string
  detail?: string
  /** Plus de 20 000 caractères : coupée, suivie de « (réponse tronquée) » */
  truncated?: boolean
}

/** Intégration nommée, telle que le serveur la décrit (telephony/modeles_api.py). */
export interface ApiActionModele {
  id: string
  /** Visible à l'étape « choisir » ; sinon seulement par le lien du partenaire */
  catalogue: boolean
  /** Secteurs d'activité visés (valeurs de l'inscription) */
  secteurs: string[]
  logiciel: string
  sous_titre: { fr: string; en: string }
  phrase: { fr: string; en: string }
  /** Ce qui préremplit le formulaire « Autre logiciel » */
  config: Pick<ApiActionConfig, 'name' | 'usage_hint' | 'url' | 'parameters'>
}

const base = (companyId: number) => `/api/companies/${companyId}/api-actions/`

/** Toutes les connexions du compte. */
export function useApiActions(companyId: number | undefined) {
  return useQuery({
    queryKey: ['api-actions', companyId],
    queryFn: async () => (await api.get<ApiAction[]>(base(companyId!))).data,
    enabled: !!companyId,
  })
}

/** Une intégration par son code, même hors catalogue (lien direct ?connect=). */
export function useApiActionModele(companyId: number | undefined, code: string | null) {
  return useQuery({
    queryKey: ['api-action-modele', companyId, code],
    queryFn: async () => (await api.get<ApiActionModele>(`${base(companyId!)}modeles/${encodeURIComponent(code!)}/`)).data,
    enabled: !!companyId && !!code && /^[a-z0-9_-]{1,60}$/.test(code),
    retry: false,
    staleTime: Infinity,
  })
}

export function useApiActionModeles(companyId: number | undefined) {
  return useQuery({
    queryKey: ['api-action-modeles', companyId],
    queryFn: async () => (await api.get<ApiActionModele[]>(`${base(companyId!)}modeles/`)).data,
    enabled: !!companyId,
    staleTime: Infinity,
  })
}

export function useSaveApiAction(companyId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id?: number; data: ApiActionInput }) =>
      (id ? await api.put<ApiAction>(`${base(companyId!)}${id}/`, data) : await api.post<ApiAction>(base(companyId!), data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-actions', companyId] }),
  })
}

export function useDeleteApiAction(companyId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`${base(companyId!)}${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-actions', companyId] }),
  })
}

/** « Créer un secret » : un nouveau secret de signature, montré une seule fois.
 *  Callizy signe toujours ses requêtes ; le logiciel du client vérifie s'il veut. */
export function useNewSigningSecret(companyId: number | undefined) {
  const qc = useQueryClient()
  return useMutation({
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-actions', companyId] }),
    mutationFn: async (id: number) => (await api.post<{ signing_secret: string }>(`${base(companyId!)}${id}/signature/`)).data,
  })
}

/** Essai d'une configuration pas encore enregistrée : le serveur appelle
 *  l'adresse avec la clé saisie (`cle`), qui n'est pas stockée. Rien n'est enregistré. */
export function useTestApiActionExample(companyId: number | undefined) {
  return useMutation({
    mutationFn: async (body: { config: Partial<ApiActionConfig>; cle?: string; values: Record<string, string>; language?: string }) =>
      (await api.post<ApiActionTestResult>(`${base(companyId!)}essai/`, body)).data,
  })
}

/** Appelle vraiment l'adresse enregistrée avec des valeurs de test.
 *  `telephone` remplace le numéro de l'appelant. */
export function useTestApiActionCall(companyId: number | undefined) {
  return useMutation({
    mutationFn: async ({ id, values, telephone, language }: { id: number; values?: Record<string, string>; telephone?: string; language?: string }) =>
      (await api.post<ApiActionTestResult>(`${base(companyId!)}${id}/essai/`, { values: values ?? {}, telephone, language })).data,
  })
}
